<script>
import ProductVariantAttributesMixin from './mixins/product-variant-attributes';
import UserMixin from '../../mixins/user';
import { propertiesOrder } from '../../models/filter';

import ProductDetailVariant from './ProductDetailVariant.vue';
import ProductSuggestions from './ProductSuggestions.vue';

export default {
  name: 'ProductDetail',
  mixins: [ProductVariantAttributesMixin, UserMixin],
  components: { ProductDetailVariant, ProductSuggestions },
  props: ['product', 'productName'],
  data() {
    return {
      showAllProperties: false,
    };
  },
  computed: {
    headerConditionnement() {
      return this.$t('presentation_ab');
    },
    headerReference() {
      return this.isMobileDevice ? this.$t('reference_ab') : this.$t('reference');
    },
    headerFournisseur() {
      return this.isMobileDevice ? this.$t('vendor_ab') : this.$t('vendor');
    },
    headerCertification() {
      return this.isMobileDevice ? this.$t('certification_ab') : this.$t('certification');
    },
    headerDilution() {
      return this.isMobileDevice ? this.$t('dilution_ab') : this.$t('dilution');
    },
    headerQuantité() {
      return this.isMobileDevice ? this.$t('quantity_ab') : this.$t('quantity');
    },
    productProperties() {
      const properties = this.product.properties.filter(
        (property) => property.values !== null && property.values.length > 0,
      );
      return properties.slice().sort(
        (a, b) => propertiesOrder.indexOf(a.name) - propertiesOrder.indexOf(b.name),
      );
    },
    visibleProperties() {
      const properties = this.productProperties;
      if (this.showAllProperties) {
        return properties;
      }
      return properties.slice(0, 2);
    },
    linkText() {
      if (this.showAllProperties) {
        return this.$t('voir_moins');
      }
      return this.$t('voir_plus');
    },
  },
  methods: {
    toggleShowAllProperties() {
      this.showAllProperties = !this.showAllProperties;
    },
    hide() {
      this.$emit('on-close');
    },
  },
};
</script>

<template>
  <div class="md:p-3 bg-white border border-black">
    <div class="bg-gray-diagomics py-2">
      <div
        class="bg-gray-diagomics-light px-2 md:px-4 py-2 text-base md:text-lg flex justify-between"
      >
        <div>
          {{ productName }}
        </div>
        <button
          @click.prevent="hide"
          type="button"
          class="bg-white rounded-md p-2 inline-flex items-center justify-center
          text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none
          focus:ring-2 focus:ring-inset focus:ring-indigo-500"
        >
          <span class="sr-only">Close menu</span>
          <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round"
              stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="p-1 md:p-4 box-border">
        <table class="table-fixed border-collapse border w-full bg-white">
          <tbody>
          <tr v-for="property in visibleProperties" :key="property.name"
              class="even:bg-gray-diagomics-light">
            <td class="w-1/3 md:w-1/4 border font-semibold p-2">
              {{ $t(property.name.toLowerCase()) }}
            </td>
            <td class="w-2/3 md:w-3/4 border p-2">
              {{ property.values && property.values.join(', ') }}
            </td>
          </tr>
          </tbody>
        </table>

        <a v-if="productProperties.length > 2"
           @click.prevent="toggleShowAllProperties"
           href="#"
           class="mt-2 text-blue-diagomics-dark block"
           v-text="linkText"></a>

        <div v-if="product.file_msds" class="mt-6">
          <a target="_blank" :href="product.file_msds"
             class="bg-white border-gray-diagomics p-2 font-semibold rounded">
            <i class="fa fa-download"></i>
            MSDS
          </a>
        </div>

        <div class="bg-white md:p-2 mt-6">
          <div v-for="format in optionFormats" :key="format" class="flex flex-col">
            <div class="bg-light-blue-900 text-white px-3 py-1">{{ format }}</div>
            <table class="table-variants table-fixed flex flex-row flex-nowrap
             border-collapse border w-full md:overflow-hidden md:table">
              <thead>
              <tr v-for="variant in getSortedVariantsByFormat(format)" :key="variant.id"
                  class="flex md:table-row flex-col flex-nowrap mb-4 md:mb-2
                   bg-gray-diagomics-light">
                <th class="md:w-1/12 border text-left pl-1 md:pl-2 h-8
                 flex items-center md:table-cell" :title="$t('presentation')">
                  {{ headerConditionnement }}
                </th>
                <th class="md:w-2/12 border text-left pl-1 md:pl-2 h-8 flex
                 items-center md:table-cell">
                  {{ headerReference }}
                </th>
                <th class="md:w-2/12 border text-left pl-1 md:pl-2 h-8
                 flex items-center md:table-cell">
                  {{ headerFournisseur }}
                </th>
                <th class="md:w-2/12 border text-left pl-1 md:pl-2 h-8
                 flex items-center md:table-cell">
                  {{ headerCertification}}
                </th>
                <th class="md:w-2/12 border text-left pl-1 md:pl-2 h-8
                 flex items-center md:table-cell">
                  {{ headerDilution}}
                </th>
                <th class="md:w-1/12 border text-left pl-1 md:pl-2 h-8
                 flex items-center md:table-cell">
                  TDS
                </th>
                <th class="md:w-24 border text-left pl-1 md:pl-2 h-12
                 flex items-center md:table-cell">{{ headerQuantité }}</th>
              </tr>
              </thead>
              <tbody class="flex-1 md:flex-none">
              <tr is="ProductDetailVariant" v-for="variant in getSortedVariantsByFormat(format)"
                  :key="variant.id" :variant="variant">
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="product.description" class="mt-6 bg-white p-4">
          {{ product.description }}
        </div>

        <div class="mt-6 bg-white p-4">
          <ProductSuggestions :product="product" />
        </div>
      </div>
    </div>
  </div>
</template>

<style>
@media (min-width: 640px) {
  .table-variants {
    display: inline-table !important;
  }

  .table-variants thead tr:not(:first-child) {
    display: none;
  }
}

.table-variants td:not(:last-child) {
  border-bottom: 0;
}
</style>
